import { useState, useEffect, useCallback } from 'react';
import { 
  VolumeSummary, 
  TimeSeriesData, 
  VolumeStats, 
  HealthStatus, 
  TimePeriod,
  ApiResponse,
  TimeSeriesResponse
} from '../types';

const API_BASE_URL = 'https://api-gatewaydash.shido.io/api/volume';

export const useVolumeData = () => {
  const [summary, setSummary] = useState<VolumeSummary | null>(null);
  const [stats, setStats] = useState<VolumeStats | null>(null);
  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async (endpoint: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || 'API request failed');
      }
      return data.data;
    } catch (err) {
      console.error(`Error fetching ${endpoint}:`, err);
      throw err;
    }
  }, []);

  const fetchSummary = useCallback(async () => {
    try {
      const data = await fetchData('/summary');
      setSummary(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch summary');
    }
  }, [fetchData]);

  const fetchStats = useCallback(async () => {
    try {
      const data = await fetchData('/stats');
      setStats(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch stats');
    }
  }, [fetchData]);

  const fetchHealth = useCallback(async () => {
    try {
      const data = await fetchData('/health');
      setHealth(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch health');
    }
  }, [fetchData]);

  const refreshData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      await Promise.all([
        fetchSummary(),
        fetchStats(),
        fetchHealth()
      ]);
    } catch (err) {
      // Error is already set by individual fetch functions
    } finally {
      setLoading(false);
    }
  }, [fetchSummary, fetchStats, fetchHealth]);

  // Initial data load
  useEffect(() => {
    refreshData();
  }, [refreshData]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const interval = setInterval(refreshData, 60000);
    return () => clearInterval(interval);
  }, [refreshData]);

  return {
    summary,
    stats,
    health,
    loading,
    error,
    refreshData
  };
};

export const useTimeSeriesData = (period: TimePeriod, count?: number) => {
  const [data, setData] = useState<TimeSeriesData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTimeSeriesData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let endpoint = `/${period}`;
      if (count) {
        endpoint += `/${count}`;
      }

      const response = await fetch(`${API_BASE_URL}${endpoint}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: TimeSeriesResponse = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch time series data');
      }

      setData(result.data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch time series data';
      setError(errorMessage);
      console.error('Error fetching time series data:', err);
    } finally {
      setLoading(false);
    }
  }, [period, count]);

  useEffect(() => {
    fetchTimeSeriesData();
  }, [fetchTimeSeriesData]);

  // Auto-refresh every 60 seconds for time series data
  useEffect(() => {
    const interval = setInterval(fetchTimeSeriesData, 60000);
    return () => clearInterval(interval);
  }, [fetchTimeSeriesData]);

  return {
    data,
    loading,
    error,
    refresh: fetchTimeSeriesData
  };
};

// Utility hook for formatting numbers
export const useFormatters = () => {
  const formatVolume = useCallback((value: string | number): string => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return '0';
    
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(2)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(2)}K`;
    } else {
      return num.toFixed(2);
    }
  }, []);

  const formatNumber = useCallback((value: string | number): string => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return '0';
    return num.toLocaleString();
  }, []);

  const formatCurrency = useCallback((value: string | number): string => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 6
    }).format(num);
  }, []);

  const formatPercentage = useCallback((value: string | number): string => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return '0%';
    return `${num.toFixed(2)}%`;
  }, []);

  return {
    formatVolume,
    formatNumber,
    formatCurrency,
    formatPercentage
  };
};
